import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import { rem } from 'polished'
import type { MouseEvent, ReactElement, ReactNode, TouchEvent } from 'react'
import { useCallback, useRef } from 'react'
import styled from 'styled-components'

const OptionCardsContainer = styled.div<{ $hasSecondaryOffer: boolean }>`
  display: grid;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  gap: ${rem(16)};
  grid-auto-flow: column;
  grid-auto-columns: ${props => props.$hasSecondaryOffer ? `minmax(${rem(270)}, 85%)` : '1fr'};
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scroll-snap-type: x mandatory;
  touch-action: pan-x;
  -webkit-overflow-scrolling: touch;

  > * {
    scroll-snap-align: start;
  }
`

const OptionCard = styled(Clickable)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${rem(16)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.S};
  padding: ${rem(16)};
  min-height: ${rem(310)};
  text-decoration: none;
  color: inherit;
`

const OptionCardSkeleton = styled(Group)`
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.S};
  padding: ${rem(16)};
  min-height: ${rem(310)};
`

const OptionCardContent = styled.div`
  flex: 1;
`

const FullWidthLoadingBox = styled(LoadingBox)`
  width: 100%;
`

const UnavailableText = styled.p`
  margin: 0;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  font-size: ${rem(14)};
  line-height: ${rem(20)};

  .unavailable-text {
    color: ${props => props.theme.palette.messaging.warning.darkForeground};
    font-weight: ${props => props.theme.font.primary.weight.superBold};
  }

  .price-text {
    font-weight: ${props => props.theme.font.primary.weight.superBold};
    margin-left: ${rem(4)};
  }

  .price-unit-text {
    margin-left: ${rem(4)};
  }
`

interface OptionCardsProps {
  children: ReactNode
  hasSecondaryOffer: boolean
}

interface OptionCardProps {
  title: string
  features?: ReactElement | false
  pricePoint?: ReactElement | false
  actionLabel: string
  isPrimary?: boolean
  to?: string
  target?: string
  onClick?: (event: MouseEvent<HTMLElement>) => void
}

interface UnavailableOptionCardProps {
  title: string
  features?: ReactElement | false
  unavailableLabel: string
  departureLabel?: string
  price?: number
  priceUnitLabel: string
  actionLabel: string
  to?: string
  target?: string
  onClick?: (event: MouseEvent<HTMLElement>) => void
}

// Ignore tiny touch jitter so taps still click, while real horizontal swipes do not navigate.
const SWIPE_CLICK_TOLERANCE = 8

export function GroupedHotelSearchTileOptionCards({
  children,
  hasSecondaryOffer,
}: OptionCardsProps) {
  const touchStartXRef = useRef(0)
  const touchStartYRef = useRef(0)
  const hasSwipedRef = useRef(false)

  const handleTouchStart = useCallback((event: TouchEvent<HTMLDivElement>) => {
    touchStartXRef.current = event.touches[0].clientX
    touchStartYRef.current = event.touches[0].clientY
    hasSwipedRef.current = false
  }, [])

  const handleTouchMove = useCallback((event: TouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0]
    const moveX = Math.abs(touch.clientX - touchStartXRef.current)
    const moveY = Math.abs(touch.clientY - touchStartYRef.current)

    hasSwipedRef.current = moveX > SWIPE_CLICK_TOLERANCE && moveX > moveY
  }, [])

  const handleClickCapture = useCallback((event: MouseEvent<HTMLDivElement>) => {
    if (!hasSwipedRef.current) return

    event.preventDefault()
    event.stopPropagation()
    hasSwipedRef.current = false
  }, [])

  return <OptionCardsContainer
    $hasSecondaryOffer={hasSecondaryOffer}
    onTouchStart={handleTouchStart}
    onTouchMove={handleTouchMove}
    onClickCapture={handleClickCapture}
  >
    {children}
  </OptionCardsContainer>
}

export function GroupedHotelSearchTileOptionCard({
  title,
  features,
  pricePoint,
  actionLabel,
  isPrimary = false,
  to,
  target,
  onClick,
}: OptionCardProps) {
  return <OptionCard
    to={to}
    target={target}
    onClick={onClick}
  >
    <OptionCardContent>
      <Group direction="vertical" gap={12}>
        <Heading variant="heading5">
          {title}
        </Heading>
        {features}
      </Group>
    </OptionCardContent>
    {pricePoint}
    <TextButton
      kind="primary"
      fit="full-width"
      nonInteractable={isPrimary}
    >
      {actionLabel}
    </TextButton>
  </OptionCard>
}

export function GroupedHotelSearchTileOptionCardSkeleton() {
  return <OptionCardSkeleton direction="vertical" gap={16} horizontalAlign="stretch">
    <OptionCardContent>
      <Group direction="vertical" gap={12}>
        <Group direction="vertical" gap={2}>
          <TextLoadingBox typography="heading5" width="70%" />
          <TextLoadingBox typography="body-medium" width="45%" />
        </Group>
        <Group direction="vertical" gap={8}>
          <TextLoadingBox typography="body-small" width="80%" />
          <TextLoadingBox typography="body-small" width="65%" />
          <TextLoadingBox typography="body-small" width="50%" />
        </Group>
      </Group>
    </OptionCardContent>
    <Group direction="vertical" gap={8}>
      <TextLoadingBox typography="body-small" width="40%" />
      <TextLoadingBox typography="heading4" width="60%" />
    </Group>
    <FullWidthLoadingBox height={48} borderRadius="S" />
  </OptionCardSkeleton>
}

export function GroupedHotelSearchTileUnavailableOptionCard({
  title,
  features,
  unavailableLabel,
  departureLabel,
  price,
  priceUnitLabel,
  actionLabel,
  to,
  target,
  onClick,
}: UnavailableOptionCardProps) {
  return <OptionCard
    to={to}
    target={target}
    onClick={onClick}
  >
    <OptionCardContent>
      <Group direction="vertical" gap={12}>
        <Heading variant="heading5">
          {title}
        </Heading>
        {features}
      </Group>
    </OptionCardContent>
    <UnavailableText>
      <span className="unavailable-text">
        {unavailableLabel}
      </span>
      {!!departureLabel && !!price && <>
        <br />
        {departureLabel}
        <span className="price-text">
          <FormatCurrency value={price} format="roundedDollar" />
        </span>
        <span className="price-unit-text">
          {priceUnitLabel}
        </span>
      </>}
    </UnavailableText>
    <TextButton
      kind="primary"
      fit="full-width"
      nonInteractable
    >
      {actionLabel}
    </TextButton>
  </OptionCard>
}
